/**
 * EDC / CDMS subject, visit-entry, CRF, review, and query statuses.
 */

import { createStatusHelpers } from "./statusHelpers";

export const EDC_SUBJECT_STATUS = {
  ACTIVE: "Active",
  COMPLETED: "Completed",
  WITHDRAWN: "Withdrawn",
  SCREEN_FAILED: "Screen Failed",
} as const;

export type EdcSubjectStatus =
  (typeof EDC_SUBJECT_STATUS)[keyof typeof EDC_SUBJECT_STATUS];

const subjectHelpers = createStatusHelpers(
  EDC_SUBJECT_STATUS,
  EDC_SUBJECT_STATUS.ACTIVE,
);

export const EDC_SUBJECT_STATUS_VALUES = subjectHelpers.VALUES;
export const tryNormalizeEdcSubjectStatus = subjectHelpers.tryNormalize;
export const normalizeEdcSubjectStatus = subjectHelpers.normalize;
export const isEdcSubjectStatus = subjectHelpers.isStatus;
export const isAnyEdcSubjectStatus = subjectHelpers.isAnyStatus;

export const EDC_VISIT_ENTRY_STATUS = {
  EMPTY: "Empty",
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  REVIEWED: "Reviewed",
  LOCKED: "Locked",
} as const;

export type EdcVisitEntryStatus =
  (typeof EDC_VISIT_ENTRY_STATUS)[keyof typeof EDC_VISIT_ENTRY_STATUS];

const visitHelpers = createStatusHelpers(
  EDC_VISIT_ENTRY_STATUS,
  EDC_VISIT_ENTRY_STATUS.EMPTY,
);

export const EDC_VISIT_ENTRY_STATUS_VALUES = visitHelpers.VALUES;
export const tryNormalizeEdcVisitEntryStatus = visitHelpers.tryNormalize;
export const normalizeEdcVisitEntryStatus = visitHelpers.normalize;
export const isEdcVisitEntryStatus = visitHelpers.isStatus;
export const isAnyEdcVisitEntryStatus = visitHelpers.isAnyStatus;

export const EDC_CRF_STATUS = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  PUBLISHED: "Published",
  ARCHIVED: "Archived",
} as const;

export type EdcCrfStatus = (typeof EDC_CRF_STATUS)[keyof typeof EDC_CRF_STATUS];

const crfHelpers = createStatusHelpers(EDC_CRF_STATUS, EDC_CRF_STATUS.DRAFT);

export const EDC_CRF_STATUS_VALUES = crfHelpers.VALUES;
export const tryNormalizeEdcCrfStatus = crfHelpers.tryNormalize;
export const normalizeEdcCrfStatus = crfHelpers.normalize;
export const isEdcCrfStatus = crfHelpers.isStatus;
export const isAnyEdcCrfStatus = crfHelpers.isAnyStatus;

export const EDC_REVIEW_STATUS = {
  PENDING_REVIEW: "Pending Review",
  APPROVED: "Approved",
  CLEAN: "Clean",
  WITH_QUERY: "With Query",
  LOCKED: "Locked",
} as const;

export type EdcReviewStatus =
  (typeof EDC_REVIEW_STATUS)[keyof typeof EDC_REVIEW_STATUS];

const reviewHelpers = createStatusHelpers(
  EDC_REVIEW_STATUS,
  EDC_REVIEW_STATUS.PENDING_REVIEW,
);

export const EDC_REVIEW_STATUS_VALUES = reviewHelpers.VALUES;
export const tryNormalizeEdcReviewStatus = reviewHelpers.tryNormalize;
export const normalizeEdcReviewStatus = reviewHelpers.normalize;
export const isEdcReviewStatus = reviewHelpers.isStatus;
export const isAnyEdcReviewStatus = reviewHelpers.isAnyStatus;

export const EDC_QUERY_STATUS = {
  OPEN: "Open",
  ANSWERED: "Answered",
  CLOSED: "Closed",
} as const;

export type EdcQueryStatus =
  (typeof EDC_QUERY_STATUS)[keyof typeof EDC_QUERY_STATUS];

const queryHelpers = createStatusHelpers(
  EDC_QUERY_STATUS,
  EDC_QUERY_STATUS.OPEN,
);

export const EDC_QUERY_STATUS_VALUES = queryHelpers.VALUES;
export const tryNormalizeEdcQueryStatus = queryHelpers.tryNormalize;
export const normalizeEdcQueryStatus = queryHelpers.normalize;
export const isEdcQueryStatus = queryHelpers.isStatus;
export const isAnyEdcQueryStatus = queryHelpers.isAnyStatus;

/** Form workflow (EDC FormStates): Draft → Submitted → Reviewed → Locked */
export const EDC_FORM_WORKFLOW_STATUS = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  REVIEWED: "Reviewed",
  LOCKED: "Locked",
} as const;

export type EdcFormWorkflowStatus =
  (typeof EDC_FORM_WORKFLOW_STATUS)[keyof typeof EDC_FORM_WORKFLOW_STATUS];

const formWorkflowHelpers = createStatusHelpers(
  EDC_FORM_WORKFLOW_STATUS,
  EDC_FORM_WORKFLOW_STATUS.DRAFT,
);

export const EDC_FORM_WORKFLOW_STATUS_VALUES = formWorkflowHelpers.VALUES;
export const tryNormalizeEdcFormWorkflowStatus =
  formWorkflowHelpers.tryNormalize;
export const normalizeEdcFormWorkflowStatus = formWorkflowHelpers.normalize;
export const isEdcFormWorkflowStatus = formWorkflowHelpers.isStatus;
export const isAnyEdcFormWorkflowStatus = formWorkflowHelpers.isAnyStatus;

/** CDMS form review: Draft | Submitted | Verified | Locked */
export const EDC_CDMS_FORM_STATUS = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  VERIFIED: "Verified",
  LOCKED: "Locked",
} as const;

export type EdcCdmsFormStatus =
  (typeof EDC_CDMS_FORM_STATUS)[keyof typeof EDC_CDMS_FORM_STATUS];

const cdmsFormHelpers = createStatusHelpers(
  EDC_CDMS_FORM_STATUS,
  EDC_CDMS_FORM_STATUS.DRAFT,
);

export const EDC_CDMS_FORM_STATUS_VALUES = cdmsFormHelpers.VALUES;
export const tryNormalizeEdcCdmsFormStatus = cdmsFormHelpers.tryNormalize;
export const normalizeEdcCdmsFormStatus = cdmsFormHelpers.normalize;
export const isEdcCdmsFormStatus = cdmsFormHelpers.isStatus;
export const isAnyEdcCdmsFormStatus = cdmsFormHelpers.isAnyStatus;

export const EDC_EXPORT_STATUS = {
  COMPLETED: "Completed",
  IN_PROGRESS: "In Progress",
  FAILED: "Failed",
} as const;

export type EdcExportStatus =
  (typeof EDC_EXPORT_STATUS)[keyof typeof EDC_EXPORT_STATUS];

const exportHelpers = createStatusHelpers(
  EDC_EXPORT_STATUS,
  EDC_EXPORT_STATUS.COMPLETED,
);

export const EDC_EXPORT_STATUS_VALUES = exportHelpers.VALUES;
export const tryNormalizeEdcExportStatus = exportHelpers.tryNormalize;
export const normalizeEdcExportStatus = exportHelpers.normalize;
export const isEdcExportStatus = exportHelpers.isStatus;
export const isAnyEdcExportStatus = exportHelpers.isAnyStatus;
